<template>
  <div id="app">
    <!-- Landing Page for non-logged-in users -->
    <router-view v-if="!isLoggedIn && $route.meta.public" />
    
    <!-- Main App for logged-in users -->
    <el-container v-else-if="isLoggedIn" class="app-container">
    <aside class="sidebar" :class="{ collapsed: sidebarCollapsed }">
      <!-- Header -->
      <div class="sidebar-header">
        <img src="./assets/jingjie_logo.png" class="sidebar-logo">
        <h3 v-if="!sidebarCollapsed" class="sidebar-brand">景杰生物</h3>
      </div>
      
      <!-- Main Navigation -->
      <nav class="sidebar-nav sidebar-nav--main">
        <el-tooltip v-for="item in mainNavItems" :key="item.key" :content="item.label" placement="right" :disabled="!sidebarCollapsed">
          <div 
            class="nav-item" 
            :class="{ active: currentMenuIndex === item.key }"
            @click="handleMenuSelect(item.key)"
          >
            <i :class="item.icon" class="nav-icon"></i>
            <span v-if="!sidebarCollapsed" class="nav-label">{{ item.label }}</span>
          </div>
        </el-tooltip>
      </nav>
      
      <!-- Bottom Section (Upload, Download, Utils, Profile) -->
      <div class="sidebar-bottom">
        <!-- Admin Upload -->
        <el-tooltip v-if="isLoggedIn && currentUser.role === 'admin'" content="Upload Data" placement="right" :disabled="!sidebarCollapsed">
          <div 
            class="nav-item" 
            :class="{ active: currentMenuIndex === 'upload' }"
            @click="handleMenuSelect('upload')"
          >
            <i class="el-icon-upload nav-icon"></i>
            <span v-if="!sidebarCollapsed" class="nav-label">Upload Data</span>
          </div>
        </el-tooltip>
        
        <!-- Download -->
        <el-tooltip content="Download Data" placement="right" :disabled="!sidebarCollapsed">
          <div 
            class="nav-item" 
            :class="{ active: currentMenuIndex === 'download' }"
            @click="handleMenuSelect('download')"
          >
            <i class="el-icon-download nav-icon"></i>
            <span v-if="!sidebarCollapsed" class="nav-label">Download Data</span>
          </div>
        </el-tooltip>
        
        <!-- Utils -->
        <div class="sidebar-utils">
          <el-tooltip content="FAQ" :placement="sidebarCollapsed ? 'right' : 'top'">
            <i class="el-icon-question util-icon" :class="{ active: $route.path === '/faq' }" @click="goToFaq"></i>
          </el-tooltip>
          <el-tooltip content="Documentation" :placement="sidebarCollapsed ? 'right' : 'top'">
            <i class="el-icon-document util-icon" :class="{ active: $route.path === '/docs' }" @click="goToDocs"></i>
          </el-tooltip>
          <el-tooltip content="Feedback" :placement="sidebarCollapsed ? 'right' : 'top'">
            <i class="el-icon-edit util-icon" :class="{ active: $route.path === '/feedback' }" @click="goToFeedback"></i>
          </el-tooltip>
        </div>
        
        <!-- Profile -->
        <div class="sidebar-footer">
          <el-tooltip :content="currentUser.username || 'Profile'" placement="right" :disabled="!sidebarCollapsed">
            <div class="nav-item" :class="{ active: $route.path === '/profile' }" @click="goToProfile">
              <i class="el-icon-user-solid nav-icon"></i>
              <span v-if="!sidebarCollapsed" class="nav-label">{{ currentUser.username || 'Profile' }}</span>
            </div>
          </el-tooltip>
        </div>
      </div>
    </aside>
    
    <!-- Sidebar Toggle Button -->
    <div class="sidebar-toggle" @click="toggleSidebar">
      <i :class="sidebarCollapsed ? 'el-icon-arrow-right' : 'el-icon-arrow-left'" class="toggle-icon"></i>
    </div>

    <el-container class="main-container">
      <el-main>
        <keep-alive>
          <router-view
            v-if="$route.meta && $route.meta.keepAlive"
            :sidebar-collapsed="sidebarCollapsed"
            :is-logged-in="isLoggedIn"
            :current-user="currentUser"
          />
        </keep-alive>
        <router-view
          v-if="!($route.meta && $route.meta.keepAlive)"
          :sidebar-collapsed="sidebarCollapsed"
          :is-logged-in="isLoggedIn"
          :current-user="currentUser"
        />
      </el-main>
    </el-container>
    
    <div v-if="false" class="floating-chat" :class="{ collapsed: isChatCollapsed }">
      <div v-if="!isChatCollapsed" class="chat-header">
        <div class="chat-title">
          <i class="el-icon-chat-dot-round chat-icon"></i>
          <span>AI Assistant</span>
        </div>
        <button 
          @click="toggleChat" 
          class="chat-toggle-btn"
          title="Close Chat"
          aria-label="Close Chat"
        >
          <i class="el-icon-close toggle-icon"></i>
        </button>
      </div>
      <div class="chat-content" v-if="!isChatCollapsed">
        <ChatInterface />
      </div>
      <button 
        v-if="isChatCollapsed"
        @click="toggleChat" 
        class="chat-icon-btn"
        title="Open Chat"
        aria-label="Open Chat"
      >
        <i class="el-icon-chat-dot-round"></i>
      </button>
    </div>
    </el-container>
  </div>
</template>

<script>
import axios from 'axios'
import ChatInterface from './components/Widgets/AssistantChatWidget.vue'

export default {
  name: 'App',
  data () {
    return {
      isChatCollapsed: true, // Initially collapsed
      sidebarCollapsed: true, // Initially expanded
      isLoggedIn: false,
      currentUser: {
        username: '',
        email: '',
        role: 'community'
      }
    }
  },
  mounted() {
    // Check if user is logged in by checking session
    this.checkLoginStatus()
  },
  computed: {
    mainNavItems() {
      const items = [
        { key: 'dashboard', label: 'Dashboard', icon: 'el-icon-data-analysis' },
        { key: 'searchStudy', label: 'Projects', icon: 'el-icon-search' },
        { key: 'searchGene', label: 'Search Protein', icon: 'el-icon-s-data' },
        { key: 'drugTarget', label: 'Drug Target Analysis', icon: 'el-icon-magic-stick' }
      ]
      // Add Assay Images for admin/enterprise users
      if (this.isLoggedIn && (this.currentUser.role === 'admin' || this.currentUser.role === 'enterprise')) {
        items.splice(3, 0, { key: 'assayImage', label: 'Assay Images', icon: 'el-icon-picture-outline' })
      }
      return items
    },
    currentMenuIndex() {
      const routeToMenuMap = {
        '/dashboard': 'dashboard',
        '/projects': 'searchStudy',
        '/gene-browser': 'searchGene',
        '/assay-images': 'assayImage',
        '/drug-target-analysis': 'drugTarget',
        '/upload': 'upload',
        '/download': 'download',
        '/profile': 'profile'
      }
      if (this.$route.path.startsWith('/project/')) {
        return 'searchStudy'
      }
      return routeToMenuMap[this.$route.path] || 'dashboard'
    }
  },
  components: {
    ChatInterface
  },
  methods: {
    handleMenuSelect (index) {
      const routeMap = {
        dashboard: '/dashboard',
        'searchStudy': '/projects',
        'searchGene': '/gene-browser',
        assayImage: '/assay-images',
        'drugTarget': '/drug-target-analysis',
        upload: '/upload',
        download: '/download',
        profile: '/profile'
      }
      const route = routeMap[index]
      if (route) {
        // Check if user needs to be logged in for upload
        if (route === '/upload' && !this.isLoggedIn) {
          this.$message.warning('Please login to access upload feature')
          this.$router.push('/profile')
          return
        }
        
        this.$router.push(route).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            throw err
          }
        })
      }
    },
    toggleChat() {
      this.isChatCollapsed = !this.isChatCollapsed;
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
    },
    goToProfile() {
      this.$router.push('/profile').catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err
        }
      })
    },
    goToDocs() {
      this.$router.push('/docs').catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err
        }
      })
    },
    goToFaq() {
      this.$router.push('/faq').catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err
        }
      })
    },
    goToFeedback() {
      this.$router.push('/feedback').catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err
        }
      })
    },
    async checkLoginStatus() {
      try {
        const response = await axios.get('/api/user/user-info')
        if (response && response.data && response.data.code === 200) {
          this.isLoggedIn = true
          this.currentUser = {
            username: response.data.data.username,
            email: response.data.data.email,
            role: response.data.data.profile?.role || 'community'
          }
          // If logged in and on home page, redirect to dashboard
          if (this.$route.path === '/') {
            this.$router.push('/dashboard')
          }
        } else {
          this.isLoggedIn = false
          this.redirectIfNotLoggedIn()
        }
      } catch (error) {
        // User not logged in
        this.isLoggedIn = false
        this.redirectIfNotLoggedIn()
      }
    },
    redirectIfNotLoggedIn() {
      // Redirect to home page if not logged in and on a protected route
      if (this.$route.path !== '/' && !this.$route.meta.public) {
        this.$router.push('/')
      }
    }
  },
  watch: {
    '$route'(to) {
      // When route changes, check if user needs to be redirected
      if (!this.isLoggedIn && !to.meta.public) {
        this.$router.push('/')
      } else if (this.isLoggedIn && to.path === '/') {
        this.$router.push('/dashboard')
      }
    }
  }
}
</script>

<style>
#app {
  height: 100vh;
  font-family: Helvetica, Helvetica Neue, Arial, -apple-system, BlinkMacSystemFont, Microsoft YaHei, noto sans, sans-serif;
}

.app-container {
  height: 100vh;
}
.el-main {
  padding: 0 !important;
}
/* ===== SIDEBAR STYLES ===== */
.sidebar {
  background: #12183f;
  position: fixed;
  top: 0;
  left: 0;
  width: 220px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  z-index: 1000;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.sidebar.collapsed {
  width: 64px;
}

/* Header */
.sidebar-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}

.sidebar.collapsed .sidebar-header {
  justify-content: center;
  padding: 16px 0;
}

.sidebar-logo {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;
}

.sidebar-brand {
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  margin: 0;
  white-space: nowrap;
}

/* Main Navigation - grows to fill space */
.sidebar-nav--main {
  flex: 1;
  padding: 8px 0;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Bottom Section - fixed at bottom */
.sidebar-bottom {
  flex-shrink: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 8px 0 0;
}

/* Nav Item - Unified Style */
.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 2px 8px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #c7d2fe;
}

.sidebar.collapsed .nav-item {
  justify-content: center;
  margin: 2px 6px;
  padding: 12px;
}

.nav-item:hover {
  background: rgba(79, 70, 229, 0.15);
  color: #fff;
}

.nav-item.active {
  background: linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.4);
}

.nav-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.nav-label {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

/* Utils */
.sidebar-utils {
  display: flex;
  justify-content: center;
  gap: 4px;
  padding: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.sidebar.collapsed .sidebar-utils {
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 6px;
}

.util-icon {
  font-size: 16px;
  color: #c7d2fe;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.util-icon:hover {
  background: rgba(79, 70, 229, 0.15);
  color: #fff;
}

.util-icon.active {
  background: rgba(79, 70, 229, 0.3);
  color: #fff;
}

/* Footer */
.sidebar-footer {
  padding: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.1);
}

.sidebar-footer .nav-item {
  margin: 0;
}

/* Sidebar Toggle */
.sidebar-toggle {
  position: fixed;
  left: 220px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 48px;
  background: #12183f;
  border-radius: 0 16px 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1001;
  transition: all 0.3s ease;
  box-shadow: 2px 0 12px rgba(79, 70, 229, 0.3);
}

.sidebar.collapsed ~ .sidebar-toggle {
  left: 64px;
}

.sidebar-toggle:hover {
  width: 28px;
  background: linear-gradient(135deg, #5b21b6 0%, #4f46e5 100%);
}

.sidebar-toggle .toggle-icon {
  color: #fff;
  font-size: 12px;
}

/* ===== MAIN CONTAINER ===== */
.main-container {
  background: #f8fafc;
  margin-left: 220px;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Shift main content when sidebar collapses */
.sidebar.collapsed ~ .main-container {
  margin-left: 64px;
}

@media (max-width: 768px) {
  .main-container {
    margin-left: 0 !important;
  }
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.page-subtitle {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

/* Main Content */
.main-content {
  padding: 32px;
  overflow: auto;
  background: #f8fafc;
}

/* ===== COMPONENT STYLES ===== */
/* Modern Card */
.modern-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.modern-card:hover {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.05);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;
  margin-bottom: 20px;
}

.card-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.card-content {
  padding: 0 24px 24px 24px;
}

/* Modern Button */
.modern-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  transition: all 0.3s ease;
}

.modern-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

/* Modern Table */
.modern-table {
  border-radius: 8px;
  overflow: hidden;
}

.modern-table .el-table__header-wrapper {
  background: #f8fafc;
}

/* ===== DASHBOARD STYLES ===== */
.dashboard-container {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  border-radius: 16px;
  color: #ffffff;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  pointer-events: none;
}

.stat-card-primary { background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); }
.stat-card-success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
.stat-card-warning { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
.stat-card-info { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); }

.stat-icon {
  font-size: 32px;
  opacity: 0.9;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 4px 0;
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
}

.content-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
}

.activity-card, .chart-card {
  height: 400px;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #f1f5f9;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #ffffff;
}

.activity-icon.success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
.activity-icon.info { background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); }
.activity-icon.warning { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }

.activity-title {
  font-weight: 500;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.activity-time {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

/* ===== PLACEHOLDER STYLES ===== */
.placeholder-card {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.placeholder-content {
  text-align: center;
  color: #64748b;
}

.placeholder-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.placeholder-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.placeholder-description {
  font-size: 14px;
  margin: 0;
}

.chart-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #64748b;
}

.chart-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.analysis-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #64748b;
}

.analysis-placeholder i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.volcano-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  margin-top: 20px;
  color: #64748b;
  border-top: 1px solid #f1f5f9;
}

.volcano-placeholder i {
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
}

/* ===== CHAT STYLES ===== */
.floating-chat {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 350px;
  height: 500px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #e2e8f0;
}

.floating-chat.collapsed {
  height: 56px;
  width: 56px;
  border-radius: 50%;
}

.floating-chat:hover {
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2), 0 8px 15px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: #ffffff;
  position: relative;
  min-height: 60px;
}

.chat-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 16px;
}

.chat-icon {
  font-size: 18px;
  opacity: 0.9;
}

.chat-toggle-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

.chat-toggle-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: scale(1.05);
}

.chat-toggle-btn:active {
  transform: scale(0.95);
}

.toggle-icon {
  font-size: 14px;
  color: #ffffff;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.chat-content {
  height: calc(100% - 60px);
  overflow: hidden;
}

.chat-icon-btn {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border: none;
  border-radius: 50%;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.chat-icon-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

.chat-icon-btn:active {
  transform: scale(0.95);
}

.chat-icon-btn i {
  font-size: 24px;
}

/* Chat Card Styles */
.chat-card {
  height: 600px;
  display: flex;
  flex-direction: column;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px 0;
  max-height: 400px;
}

.empty-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #64748b;
}

.empty-chat i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.message {
  margin-bottom: 16px;
  display: flex;
}

.message-user {
  justify-content: flex-end;
}

.message-assistant {
  justify-content: flex-start;
}

.message-content {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 12px;
  position: relative;
}

.message-user .message-content {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: #ffffff;
  border-bottom-right-radius: 4px;
}

.message-assistant .message-content {
  background: #f1f5f9;
  color: #1e293b;
  border-bottom-left-radius: 4px;
}

.message-time {
  font-size: 11px;
  opacity: 0.7;
  margin-top: 4px;
  display: block;
}

.chat-input {
  padding-top: 20px;
  border-top: 1px solid #f1f5f9;
}

.modern-input .el-input__inner {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}
/*
::-webkit-scrollbar { width: 6px; }
::-webkit-scrollbar-track { background: #f1f5f9; }
::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
*/
.el-table__fixed{
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.12) !important;
}

/* ===== UTILITIES ===== */
/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Apply animation to sidebar */
.sidebar {
  animation: slideInLeft 0.4s ease;
}

/* Staggered animation for nav items */
.nav-item {
  animation: fadeIn 0.3s ease backwards;
}

.nav-item:nth-child(1) { animation-delay: 0.05s; }
.nav-item:nth-child(2) { animation-delay: 0.08s; }
.nav-item:nth-child(3) { animation-delay: 0.11s; }
.nav-item:nth-child(4) { animation-delay: 0.14s; }
.nav-item:nth-child(5) { animation-delay: 0.17s; }
.nav-item:nth-child(6) { animation-delay: 0.20s; }

/* ===== RESPONSIVE DESIGN ===== */
@media (max-width: 768px) {
  .sidebar-toggle {
    display: none;
  }
  
  .sidebar {
    width: 100% !important;
    position: fixed;
    z-index: 1000;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }
  
  .sidebar.open {
    transform: translateX(0);
  }
  
  .content-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
  
  .header {
    padding: 16px 20px;
  }
  
  .main-content {
    padding: 20px;
  }
  
  .floating-chat {
    width: calc(100vw - 40px);
    right: 20px;
    left: 20px;
    max-width: 350px;
  }
  
  .floating-chat.collapsed {
    width: 56px;
  }
}
</style>

